import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowRightEndOnRectangleIcon, UserPlusIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Loader } from '@mantine/core';
import { apiService } from '../services/api';
import InlineFeedback from '../components/InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { ReactComponent as Logo } from '../logo.svg';

interface AuthPageProps {
  mode: 'signin' | 'signup';
}

// Dedicated /signin and /signup pages. Both are email-first: they trigger a
// 30-minute magic link email; credentials are handed over on /auth/verify.
const AuthPage: React.FC<AuthPageProps> = ({ mode }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const feedback = useFeedback();

  const isSignIn = mode === 'signin';
  // Portal sign-in skips the email. Portal verifies the user and hands back a /auth/verify link.
  const portalURL = (process.env.REACT_APP_PORTAL_URL || '').replace(/\/+$/, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      feedback.showError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    feedback.clear();
    try {
      if (isSignIn) {
        await apiService.requestLoginLink(email);
      } else {
        await apiService.signUp(email);
      }
      setSentTo(email);
    } catch (error: any) {
      if (error.response?.status === 429) {
        feedback.showError('Too many requests. Try again later.');
      } else {
        feedback.showError(isSignIn ? 'Failed to send sign-in link. Try again later.' : 'Failed to sign up. Try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-body-bg flex flex-col items-center justify-center px-4 py-12">
      <a href="/" className="mb-8">
        <Logo className="h-10 w-auto" style={{ color: '#F8F9FA' }} />
      </a>

      <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full border border-secondary shadow-xl">
        {sentTo ? (
          <div className="flex flex-col items-center text-center gap-4">
            <EnvelopeIcon className="w-10 h-10 text-primary" />
            <h2 className="text-2xl font-bold text-heading">Check your inbox</h2>
            <p className="text-body-color">
              {isSignIn
                ? <>If a saved instance exists for <strong className="text-heading">{sentTo}</strong>, we sent a sign-in link.</>
                : <>If <strong className="text-heading">{sentTo}</strong> is eligible, we sent an access link for your new instance.</>}
            </p>
            <p className="text-sm text-body-color/70">Links expire in 30 minutes.</p>
            <button
              onClick={() => setSentTo('')}
              className="btn-secondary"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              {isSignIn
                ? <ArrowRightEndOnRectangleIcon className="w-7 h-7 text-primary" />
                : <UserPlusIcon className="w-7 h-7 text-primary" />}
              <h2 className="text-2xl font-bold text-heading">
                {isSignIn ? 'Sign in' : 'Sign up'}
              </h2>
            </div>
            <p className="text-body-color/70 text-sm mb-6">
              {isSignIn
                ? 'Enter your email. We will send a sign-in link for your saved instance. The link works once. It expires in 30 minutes.'
                : 'Enter your email. We will send an access link for your new instance. The link expires in 30 minutes.'}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="auth-email" className="block text-sm text-secondary mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  id="auth-email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <InlineFeedback feedback={feedback.feedback} />
              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-primary-full py-3 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader size="xs" color="white" />
                    Sending...
                  </>
                ) : (
                  isSignIn ? 'Email me a sign-in link' : 'Create my instance'
                )}
              </button>
            </form>
            {portalURL && (
              <>
                <div className="flex items-center gap-3 my-6 text-xs text-body-color/50">
                  <div className="flex-1 border-t border-secondary" />
                  or
                  <div className="flex-1 border-t border-secondary" />
                </div>
                <a
                  href={`${portalURL}/connect/live`}
                  className="btn-secondary w-full py-3 flex items-center justify-center gap-2"
                >
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  Continue with Alchemist Portal
                </a>
              </>
            )}
          </>
        )}

        <div className="mt-6 pt-6 border-t border-secondary text-center text-sm text-body-color/70">
          {isSignIn ? (
            <>
              New to Alchemist?{' '}
              <Link to="/signup" className="text-primary hover:text-primary-hover transition-colors">
                Sign up
              </Link>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <Link to="/signin" className="text-primary hover:text-primary-hover transition-colors">
                Sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
